import { useState, useCallback, useRef, useEffect } from "react";
import { Order } from "@/types/supabase";
import { fetchWithAuth } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { toastHandler } from "@/lib/ToastHandler";
import { HistoryQuery, historyQueryString } from "@/lib/orderHistoryQuery";

type HistoricalOrder = Omit<Order, "branch"> & {
  branch?: { id: number; name: string; brand?: { id: number; name: string } };
};

export const useHistoricalOrders = () => {
  const [items, setItems] = useState<HistoricalOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const currentParams = useRef<HistoryQuery>({});
  const requestVersion = useRef(0);
  const { getAccessToken } = useAuth();
  const tokenGetter = useRef(getAccessToken);
  tokenGetter.current = getAccessToken;

  useEffect(
    () => () => {
      requestVersion.current += 1;
    },
    [],
  );

  const fetchOrders = useCallback(async (params: HistoryQuery) => {
    const version = ++requestVersion.current;
    setLoading(true);
    setError(null);
    currentParams.current = params;
    try {
      const token = await tokenGetter.current();
      const response = await fetchWithAuth(
        `/order/orders/history?${historyQueryString(params)}`,
        token,
      );
      if (version !== requestVersion.current) return;
      const data: HistoricalOrder[] = response?.data || [];
      setItems((previous) =>
        (params.page || 1) === 1 ? data : [...previous, ...data],
      );
      setTotalCount(response?.total || 0);
    } catch (err) {
      if (version !== requestVersion.current) return;
      const message =
        err instanceof Error
          ? err.message
          : "Error al cargar el historial de órdenes";
      setError(message);
      toastHandler({
        description: message,
        variant: "destructive",
        title: "Error",
      });
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, []);

  const refetch = useCallback(
    () => fetchOrders({ ...currentParams.current, page: 1 }),
    [fetchOrders],
  );
  return {
    items,
    loading,
    error,
    totalCount,
    hasMore: items.length < totalCount,
    fetchOrders,
    refetch,
  };
};
